<template>
  <div>
    <div class="row">
      <div class="col-md-12 subtitle">
        <div class="panel-heading">
          <h3 class="panel-title">
            <slot></slot>
          </h3>
        </div>
      </div>
      <div class="col-md-12">
        <div class="line-separator bottom-margin"></div>
      </div>
    </div>

    <no-upcoming-transactions v-if="notesTransactions.length === 0" />

    <div v-else>
      <div
        v-for="(nt, index) in notesTransactions"
        :key="index"
        class="panel panel-default"
      >
        <upcoming-transaction-data
          :transaction-id="nt.transaction.transactionId"
          :header-message="headerMessage"
          :alert-message="getAlertMessage(nt)"
          :maturity-date="nt.transaction.maturityDate"
          :note-id="nt.transaction.note.noteId"
          :payment-type="nt.transaction.type"
          :amount="nt.transaction.amount"
        >
          <template>
            <div v-if="!actionProvided" role="alert" class="alert alert-info">
              <p>
                <em class="icon icon-info pr-sm"></em>{{ headerMessage }}
                <strong>{{ getAlertMessage(nt) }}</strong> on
                {{ formatDate(nt.transaction.maturityDate) }}.
              </p>
              <p>
                Please note that you're able to change this payment using the
                options below.
              </p>
            </div>
            <div v-else role="alert" class="alert alert-success">
              <p>
                <em class="icon-check pr-sm"></em>You have chosen to
                <strong>{{ getAlertMessage(nt) }}</strong> on
                {{ formatDate(nt.transaction.maturityDate) }}.
              </p>
              <p>
                Please note that you're able to change this payment using the
                options below.
              </p>
            </div>
          </template>
        </upcoming-transaction-data>

        <div class="transaction-reinvest">
          <div class="row">
            <div class="col-md-6 left-padding">
              <label class="value">Select from options below</label>
            </div>

            <div
              class="col-md-6 left-padding"
              v-if="isReinvestActionTaken(nt.action)"
            >
              <label class="value">Rate and term</label>
            </div>

            <div
              class="col-md-6 left-padding"
              v-if="isDonateActionTaken(nt.action)"
            >
              <label class="value">Donation amount</label>
            </div>

            <div
              class="col-md-6 left-padding"
              v-if="isCashoutActionTaken(nt.action)"
            >
              <label class="value">Select bank account</label>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6 left-padding">
              <select
                v-model="nt.action"
                @change="
                  $emit('actionChanged', {
                    action: nt.action,
                    index,
                    actionPreviousTaken: actionProvided,
                  })
                "
                name="action"
                class="form-control input-lg"
                required
              >
                <option
                  v-for="(action, index) in allowedActions"
                  :value="action"
                  :key="index"
                  >{{ action.text }}
                </option>
              </select>
            </div>

            <div
              class="col-md-6 left-padding"
              v-if="isReinvestActionTaken(nt.action)"
            >
            <CustomRatesAndTermsForInvestor
            :nt="nt"
            :action-provided="actionProvided"
            @setRateAndTerm="setRateAndTerm"
             />
            </div>

            <div
              class="col-md-6 left-padding"
              v-if="isDonateActionTaken(nt.action)"
            >
              <input
                v-model="nt.amountForDonation"
                :disabled="true"
                class="form-control input-lg"
                required
              />
            </div>

            <div
              class="col-md-6 left-padding"
              v-if="isCashoutActionTaken(nt.action)"
            >
              <select
                v-model="nt.fundingSource"
                name="fundingSource"
                class="form-control input-lg"
                required
              >
                <option
                  disabled
                  hidden
                  v-if="fs.removed"
                  v-for="(fs, index) in fundingSources"
                  :value="fs"
                  :key="index"
                  >{{ fs.name }}</option
                >
                <option
                  v-if="!fs.removed"
                  v-for="(fs, index) in fundingSources"
                  :value="fs"
                  :key="index"
                  >{{ fs.name }}</option
                >
              </select>

              <span
                v-if="shouldSubmitButtonBeDisabled(nt)"
                class="error-feedback"
                >Please choose a bank account above or add one on the
                <router-link
                  :to="{ name: 'AddFundingSource' }"
                  title="Add a bank account"
                  class="hyperlink"
                  replace
                  >bank account page</router-link
                ></span
              >
            </div>
          </div>
        </div>

        <div class="row" v-if="isReinvestActionTaken(nt.action)">
          <div class="col-md-12">
            <div class="checkbox c-checkbox">
              <label>
                <input
                  type="checkbox"
                  v-model="nt.termsOfConditionAcceptance"
                  value=""
                />
                <span class="fa fa-check"></span>
                <terms-of-conditions hide-state-disclosures />
              </label>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <button
              type="submit"
              class="btn btn-default btn-lg"
              :disabled="submitsDisabled || shouldSubmitButtonBeDisabled(nt)"
              @click="$emit('submit', nt, rateAndTerm)"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import NoUpcomingTransactions from '@/components/UpcomingTransactions/NoUpcomingTransactions';
import UpcomingTransactionsData from '@/components/UpcomingTransactions/UpcomingTransactionsData';
import CustomRatesAndTermsForInvestor from '@/components/UpcomingTransactions/CustomRatesAndTermsForInvestor';
import TermsOfConditions from '@/components/TermsOfConditions';
import { formatDate } from '@/util';

import Actions from '@/data/static/upcomingTransactionActions'; // eslint-disable-line no-unused-vars
import TransactionTypes from '@/data/static/transactionTypes'; // eslint-disable-line no-unused-vars

const mix = {
  methods: {
    formatDate,
  },
};

export default {
  name: 'UpcomingTransactions',
  mixins: [mix],
  components: {
    'no-upcoming-transactions': NoUpcomingTransactions,
    'upcoming-transaction-data': UpcomingTransactionsData,
    'terms-of-conditions': TermsOfConditions,
    CustomRatesAndTermsForInvestor,
  },
  props: {
    notesTransactions: {
      type: Array,
      required: true,
      default: () => [],
    },
    allowedActions: {
      type: Array,
      required: true,
      default: () => [],
    },
    fundingSources: {
      type: Array,
      required: true,
      default: () => [],
    },
    yearRates: {
      type: Array,
      required: true,
      default: () => [],
    },
    submitsDisabled: {
      type: Boolean,
      required: true,
      default: false,
    },
    actionProvided: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  computed: {
    headerMessage() {
      return 'According to your default preference your money will be';
    },
  },
  data() {
    return {
      rateAndTerm: {
        id: 0,
        label: '0.00%, 0 year',
        manageRateAndTermGroupsId: 0,
        precedence: 0,
        rate: 0,
        term: 0,
      },
    };
  },
  methods: {
    setRateAndTerm(rateAndTerm) {
      this.rateAndTerm = rateAndTerm;
    },
    getAlertMessage(upcomingTransaction) {
      const { type, actionTaken } = upcomingTransaction.transaction;
      const isActionTaken = actionTaken !== undefined && actionTaken !== null;
      if (
        type === TransactionTypes.INT_PAID ||
        type === TransactionTypes.SELL_MATURE
      ) {
        return isActionTaken ? 'repay account' : 'repaid';
      } else if (
        type === TransactionTypes.INT_DONATE ||
        type === TransactionTypes.SELL_DONATE
      ) {
        return isActionTaken
          ? 'donate to Calvert Impact Capital'
          : 'donated to Calvert Impact Capital';
      }
      return isActionTaken
        ? 'reinvest in a new note'
        : 'reinvested in a new note';
    },
    shouldSubmitButtonBeDisabled(noteTransaction) {
      const { action } = noteTransaction;
      if (action.name === Actions.cashout.name) {
        return (
          noteTransaction.fundingSource === undefined ||
          noteTransaction.fundingSource === null
        );
      } else if (action.name === Actions.reinvest.name) {
        return !noteTransaction.termsOfConditionAcceptance;
      }

      return false;
    },
    isCashoutActionTaken(action) {
      return action.name === Actions.cashout.name;
    },
    isDonateActionTaken(action) {
      return action.name === Actions.donate.name;
    },
    isReinvestActionTaken(action) {
      return action.name === Actions.reinvest.name;
    },
  },
};
</script>
<style scoped>
.panel-default {
  padding: 20px 30px;
}
.panel-heading {
  color: #444;
  background-color: #ffffff;
  border-color: #cfdbe2;
  border-bottom: 0;
  padding: 0;
}
.alert {
  padding: 20px 10px 20px 10px;
}
label.title {
  color: #909fa6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
label.value {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
select,
input {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 40px;
}
.transaction-reinvest {
  margin: 10px 20px 10px 20px;
}
.line-separator {
  padding: 8px;
  line-height: 1.52857143;
  vertical-align: top;
  border-top: 1px solid #eee;
}
.bottom-margin {
  margin-bottom: 5px;
}
.left-padding {
  padding-left: 0;
}
.top-margin {
  margin-top: 15px;
}
.subtitle {
  margin: 7px 0 7px 0;
  color: #817e7d;
}
.btn {
  margin-left: 5px;
}
.no-transactions {
  color: #f78a0e;
}
.hyperlink {
  color: #f05050;
  text-decoration: underline;
}
.panel-title .fa {
  margin-right: 10px;
}
.panel .panel .panel-title {
  margin-bottom: 15px;
  font-size: 16px;
}
.checkbox {
  margin-left: 10px;
  margin-bottom: 15px;
  margin-left: 5px;
}
.c-checkbox span {
  margin-left: -28px;
}
</style>



// WEBPACK FOOTER //
// index.vue?91a0a7d8